/**
 * Format a number as Australian currency
 */
export const formatCurrency = (value: number): string => {
  // Use decimal places for small values (like subsidies)
  const hasDecimals = value < 100 && value % 1 !== 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(value);
};

/**
 * Format a number with commas and optional decimal places
 */
export const formatNumber = (value: number, decimals: number = 1): string => {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format a number as a percentage
 */
export const formatPercent = (value: number): string => {
  return `${Math.round(value)}%`;
};

/**
 * Format hours with appropriate unit
 */
export const formatHours = (hours: number): string => {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} min`;
  }
  return `${formatNumber(hours)} hrs`;
};

/**
 * Format weight with appropriate unit (kg or tonnes)
 */
export const formatWeight = (kg: number): string => {
  if (kg >= 1000) {
    return `${formatNumber(kg / 1000)} tonnes`;
  }
  return `${formatNumber(kg)} kg`;
};

/**
 * Format volume with appropriate unit (L or kL)
 */
export const formatVolume = (litres: number): string => {
  if (litres >= 1000) {
    return `${formatNumber(litres / 1000)} kL`;
  }
  return `${formatNumber(litres)} L`;
};

/**
 * Format energy with appropriate unit (kJ or MJ)
 */
export const formatEnergy = (kj: number): string => {
  if (kj >= 1000) {
    return `${formatNumber(kj / 1000)} MJ`;
  }
  return `${formatNumber(kj)} kJ`;
};

/**
 * Format FTE with one decimal place
 */
export const formatFTE = (value: number): string => {
  return `${formatNumber(value, 1)} FTE`;
};

/**
 * Format large numbers with K/M suffixes
 */
export const formatCompactNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${formatNumber(value / 1000000)}M`;
  }
  if (value >= 1000) {
    return `${formatNumber(value / 1000)}K`;
  }
  return formatNumber(value, 0);
};

/**
 * Format currency for input display (no symbol for editing)
 */
export const formatCurrencyInput = (value: number): string => {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Parse currency string to number
 */
export const parseCurrencyInput = (value: string): number => {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
};
